/**
 * Live Data Status: is live data arriving, per ship and data stream.
 *
 * Reads `/api/live/status` (the Live API's `GET /status`, judged against
 * SONAR's thresholds) every 30 s while the section is on screen. The fleet is
 * one `.data-table`, grouped by brand; a click on a cell opens a card with the
 * stream's signals. Sketch: docs/design/sketches/live-status-matrix.html.
 *
 * The "EOS Live session overlay" switch adds a LIVE chip to each ship with an
 * EOS Live session (`/api/live/sessions`, read only while the switch is on; the
 * backend asks the Optimization Service once a minute at most). Sketch:
 * docs/design/sketches/live-session-overlay.html, option A.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api/client.ts';
import { listShipNames, recordVisit } from '../../api/endpoints.ts';
import { userMessage } from '../../api/errors.ts';
import { LightElement } from '../../lit-base.ts';
import { registerSectionRoute } from '../../state/app-route.ts';
import { hasFlag } from '../../state/feature-flags.ts';
import { StoreController } from '../../state/controller.ts';
import { activeSection, myAccess, shipMeta } from '../../state/store.ts';
import { fmtUtcDatetime } from '../../utils/dates.ts';
import { shipBrand, shipLabel } from '../../utils/ships.ts';
import { applyProposedPlot } from '../assistant/apply-plot.ts';
import type { PlotSpec } from '../../api/types.ts';
import {
  cellText,
  formatAge,
  frozenSignals,
  groupRows,
  nextSort,
  NAUTICAL_COLUMNS,
  OPTIONAL,
  RANK,
  sessionStatusWord,
  shownAge,
  STREAMS,
  WINDOWS,
  WORD,
  type Filter,
  type LiveSessions,
  type LiveStatus,
  type Ship,
  type Sort,
  type Stream,
  type Verdict,
} from './live-status-model.ts';

const REFRESH_S = 30;
const LIVE_KEY = 'live-status.eos-live-overlay';
const SESSION = 'eos-live'; // the card key's second part for a ship's session

function readOverlay(): boolean {
  try {
    return localStorage.getItem(LIVE_KEY) === '1';
  } catch {
    return false;
  }
}

registerSectionRoute({ section: 'live-status' });

@customElement('live-status-page')
export class LiveStatusPage extends LightElement {
  private store = new StoreController(this, [activeSection, shipMeta]);

  @state() private data: LiveStatus | null = null;
  @state() private error: string | null = null;
  @state() private loading = false;
  @state() private since = 0; // seconds since the last answer
  @state() private filter: Filter = 'all';
  @state() private query = '';
  @state() private sort: Sort = null;
  @state() private open: string | null = null; // "ship/stream"
  @state() private exporting = false;
  @state() private overlay = readOverlay();
  @state() private sessions: LiveSessions | null = null;
  private sessionsAt = 0; // ms; the backend caches a minute, so asking more often gains nothing
  private timer: number | undefined;
  private wasShown = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.timer = window.setInterval(() => this.tick(), 1000);
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('scroll', this.place, true);
    window.addEventListener('resize', this.place);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.clearInterval(this.timer);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
    window.removeEventListener('scroll', this.place, true);
    window.removeEventListener('resize', this.place);
  }

  private get shown(): boolean {
    return activeSection.value === 'live-status';
  }

  protected override willUpdate(): void {
    // Opening the section asks at once; leaving it stops asking.
    if (this.shown && !this.wasShown) {
      void this.load();
      recordVisit('live-status').catch(() => {}); // the usage log never costs the page an error
    }
    if (!this.shown) this.open = null;
    this.wasShown = this.shown;
  }

  protected override updated(): void {
    this.place();
  }

  private tick(): void {
    if (!this.shown || document.visibilityState !== 'visible') return;
    this.since += 1;
    if (this.since >= REFRESH_S && !this.loading) void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    // Names are static. `loadShips()` sets them only after the history source answers.
    if (!Object.keys(shipMeta.value).length) void listShipNames().then((n) => shipMeta.set(n), () => {});
    if (this.overlay && Date.now() - this.sessionsAt >= 60_000) void this.loadSessions();
    try {
      this.data = await api<LiveStatus>('/live/status');
      this.error = null;
      this.since = 0;
    } catch (e) {
      this.error = userMessage(e, 'The live data status could not be read.');
      this.since = 0;
    } finally {
      this.loading = false;
    }
  }

  private async loadSessions(): Promise<void> {
    this.sessionsAt = Date.now();
    try {
      this.sessions = await api<LiveSessions>('/live/sessions');
    } catch {
      this.sessions = { available: false, sessions: {} };
    }
  }

  private setOverlay(on: boolean): void {
    this.overlay = on;
    if (!on && this.open?.endsWith(`/${SESSION}`)) this.open = null;
    try {
      localStorage.setItem(LIVE_KEY, on ? '1' : '0');
    } catch {
      /* a remembered switch is a convenience */
    }
    if (on) void this.loadSessions();
  }

  private age(s: number | null | undefined): string {
    return s == null ? '-' : formatAge(s + this.since);
  }

  // ── The card ─────────────────────────────────────────────

  private onDocClick = (e: MouseEvent): void => {
    if (!this.open) return;
    const t = e.target as Element;
    if (t.closest('.lst-pop') || t.closest('.lst-cell') || t.closest('.lst-live')) return;
    this.open = null;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !this.open) return;
    const key = this.open;
    this.open = null;
    void this.updateComplete.then(() =>
      this.querySelector<HTMLElement>(`[data-cell="${CSS.escape(key)}"]`)?.focus(),
    );
  };

  /** Beside its cell, inside the window; follows the cell on scroll and resize. */
  private place = (): void => {
    const pop = this.querySelector<HTMLElement>('.lst-pop');
    const cell = this.open && this.querySelector<HTMLElement>(`[data-cell="${CSS.escape(this.open)}"]`);
    if (!pop || !cell) return;
    const r = cell.getBoundingClientRect();
    if (r.bottom < 0 || r.top > innerHeight) {
      this.open = null;
      return;
    }
    const w = pop.offsetWidth, h = pop.offsetHeight, m = 8, edge = 16;
    let x = r.right + m, y = r.top - 4;
    if (x + w > innerWidth - edge) x = r.left - w - m;
    if (x < edge) {
      x = Math.max(edge, Math.min(r.left, innerWidth - w - edge));
      y = r.bottom + m;
    }
    if (y + h > innerHeight - edge) y = Math.max(edge, innerHeight - h - edge);
    pop.style.left = `${x}px`;
    pop.style.top = `${y}px`;
  };

  private toggle(key: string): void {
    this.open = this.open === key ? null : key;
  }

  private async openInExplore(id: string, st: Stream, sectionKey: string): Promise<void> {
    const ordered = Object.entries(st.signals).sort(
      ([, a], [, b]) => RANK[b.status] - RANK[a.status] || (b.age_seconds ?? 0) - (a.age_seconds ?? 0),
    );
    const columns = ordered
      .slice(0, 6)
      .map(([name]) => (sectionKey === 'nautical' ? NAUTICAL_COLUMNS[name] ?? name.toLowerCase() : name.toLowerCase()));
    // A window that shows the gap: twice the oldest late reading, 1 h at least.
    const oldest = shownAge(st) ?? 0;
    const window = WINDOWS.find((w) => parseInt(w, 10) * 3600 >= 2 * oldest) ?? '48h';
    const spec: PlotSpec = {
      ship_id: id,
      source: 'live',
      live_window: window,
      plot_type: 'line',
      y_left: columns,
      y_right: [],
      hist_columns: [],
    };
    this.open = null;
    try {
      await applyProposedPlot(spec);
    } catch (e) {
      this.error = userMessage(e, 'Explore could not open these signals.');
    }
  }

  /** The board as it is now, chips and search applied, as a PDF report. */
  private async exportPdf(): Promise<void> {
    if (!this.data || this.exporting) return;
    this.exporting = true;
    const q = this.query.trim();
    const filter = [this.filter !== 'all' && `Filter: ${WORD[this.filter]}`, q && `search "${q}"`]
      .filter(Boolean)
      .join(' · ');
    try {
      const { saveStatusPdf } = await import('./live-status-pdf.ts');
      await saveStatusPdf({
        data: this.data,
        ships: this.visibleShips(),
        filter: filter || null,
        label: shipLabel,
        brand: shipBrand,
        exportedBy: myAccess.value?.display_name ?? null,
      });
    } catch (e) {
      this.error = userMessage(e, 'The PDF could not be made.');
    } finally {
      this.exporting = false;
    }
  }

  // ── Drawing ──────────────────────────────────────────────

  private mark(v: Verdict): TemplateResult {
    return html`<span class="lst-mk is-${v}" aria-hidden="true"></span>`;
  }

  private visibleShips(): Array<[string, Ship]> {
    const q = this.query.trim().toLowerCase();
    return Object.entries(this.data?.ships ?? {}).filter(
      ([id, s]) =>
        (this.filter === 'all' || s.status === this.filter) &&
        (!q || id.toLowerCase().includes(q) || shipLabel(id).toLowerCase().includes(q)),
    );
  }

  private renderChips(): TemplateResult {
    const ships = Object.values(this.data?.ships ?? {});
    const n = (v: Verdict) => ships.filter((s) => s.status === v).length;
    const chips: Array<[Filter, string, number]> = [
      ['all', 'All', ships.length],
      ['critical', WORD.critical, n('critical')],
      ['warning', WORD.warning, n('warning')],
      ['missing', WORD.missing, n('missing')],
      ['ok', WORD.ok, n('ok')],
    ];
    return html`<div class="lst-chips" role="group" aria-label="Filter by status">
      ${chips.map(
        ([f, label, count]) => html`<button
          type="button"
          class="lst-chip ${this.filter === f ? 'is-on' : ''}"
          aria-pressed=${this.filter === f}
          @click=${() => (this.filter = this.filter === f ? 'all' : f)}
        >
          ${f === 'all' ? nothing : this.mark(f)}${label} <b>${count}</b>
        </button>`,
      )}
    </div>`;
  }

  private sortHead(key: string, label: string, cls: string, rowspan?: number): TemplateResult {
    const on = this.sort?.key === key;
    const caret = on ? (this.sort!.dir === 'asc' ? ' ▲' : ' ▼') : '';
    return html`<th
      scope="col"
      rowspan=${rowspan ?? nothing}
      class="${cls} data-table-sort ${on ? 'sorted' : ''}"
      aria-sort=${on ? (this.sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title=${key === 'name' ? 'Sort by ship' : `Sort by ${label}, worst first`}
      @click=${() => (this.sort = nextSort(this.sort, key))}
    >${label}${caret}</th>`;
  }

  private brandMeta(rows: Array<[string, Ship]>): TemplateResult {
    const n = (v: Verdict) => rows.filter(([, s]) => s.status === v).length;
    const parts: TemplateResult[] = [html`${rows.length} ship${rows.length === 1 ? '' : 's'}`];
    for (const v of ['critical', 'warning', 'missing'] as const) {
      if (n(v)) parts.push(html` · <span class="lst-w-${v}">${n(v)} ${WORD[v].toLowerCase()}</span>`);
    }
    return html`${parts}`;
  }

  private renderCell(id: string, key: string, st: Stream | undefined): TemplateResult {
    if (!st) {
      const name = OPTIONAL[key];
      return html`<td class="lst-td is-none" title=${name ? `This ship has no ${name}` : nothing}>
        <span class="lst-none">${name ? `No ${name}` : ''}</span>
      </td>`;
    }
    const cellKey = `${id}/${key}`;
    const label = cellText(st, (s) => this.age(s));
    return html`<td class="lst-td is-${st.status} ${this.sort?.key === key ? 'sorted' : ''} ${this.open === cellKey ? 'is-open' : ''}">
      <button
        type="button"
        class="lst-cell"
        data-cell=${cellKey}
        aria-haspopup="dialog"
        aria-expanded=${this.open === cellKey}
        aria-label="${shipLabel(id)}, ${key}: ${WORD[st.status]}, ${label}"
        @click=${() => this.toggle(cellKey)}
      >
        ${this.mark(st.status)}<span class="lst-age">${label}</span>
      </button>
    </td>`;
  }

  private renderTable(): TemplateResult {
    const groups = groupRows(this.visibleShips(), this.sort, shipLabel, shipBrand);
    const order = !this.sort
      ? 'by brand and name'
      : this.sort.key === 'name'
        ? `sorted by ship, ${this.sort.dir === 'asc' ? 'A to Z' : 'Z to A'}`
        : `sorted by ${STREAMS.find(([k]) => k === this.sort!.key)?.[1]}, ${this.sort.dir === 'asc' ? 'worst' : 'best'} first`;
    const shown = groups.reduce((n, [, rows]) => n + rows.length, 0);
    return html`<div class="data-table-bar">
        <span class="data-table-count">${shown} of ${this.data?.ship_count ?? 0} ships · ${order}</span>
      </div>
      <div class="data-table-scroll lst-scroll">
        <table class="data-table lst-table">
          <colgroup>
            <col class="lst-col-ship" />
            ${STREAMS.map(() => html`<col class="lst-col-stream" />`)}
          </colgroup>
          <thead>
            <tr class="lst-group">
              ${this.sortHead('name', 'Ship', 'data-table-key data-table-first', 2)}
              <th scope="colgroup" colspan=${STREAMS.length} class="lst-streams">Data streams</th>
            </tr>
            <tr>
              ${STREAMS.map(([k, label]) => this.sortHead(k, label, 'data-table-key lst-th-stream'))}
            </tr>
          </thead>
          <tbody>
            ${groups.length
              ? groups.map(
                  ([brand, rows]) => html`<tr class="lst-brand">
                      <th colspan=${STREAMS.length + 1} scope="rowgroup">
                        <span class="lst-brand-name">${brand}</span><span class="lst-brand-meta">${this.brandMeta(rows)}</span>
                      </th>
                    </tr>
                    ${rows.map(
                      ([id, ship]) => html`<tr>
                        <th scope="row" class="data-table-key data-table-first lst-ship">
                          <span class="lst-ship-name">${shipLabel(id)}</span>${this.liveChip(id)}<span class="lst-ship-id">${id}</span>
                        </th>
                        ${STREAMS.map(([k]) => this.renderCell(id, k, ship.sections[k]))}
                      </tr>`,
                    )}`,
                )
              : html`<tr>
                  <td class="lst-empty" colspan=${STREAMS.length + 1}>
                    No ship matches this filter${this.query ? ' and search' : ''}.
                  </td>
                </tr>`}
          </tbody>
        </table>
      </div>`;
  }

  private liveChip(id: string): TemplateResult | typeof nothing {
    if (!this.overlay || !this.sessions?.sessions[id]) return nothing;
    const key = `${id}/${SESSION}`;
    return html`<button
      type="button"
      class="lst-live"
      data-cell=${key}
      aria-haspopup="dialog"
      aria-expanded=${this.open === key}
      aria-label="${shipLabel(id)}: EOS Live session"
      @click=${() => this.toggle(key)}
    ><i aria-hidden="true"></i>LIVE</button>`;
  }

  private renderOverlaySwitch(): TemplateResult {
    const on = this.overlay;
    const ships = this.data?.ships ?? {};
    const count = Object.keys(this.sessions?.sessions ?? {}).filter((id) => id in ships).length;
    const shown = on && this.sessions?.available;
    return html`<button
      type="button"
      class="dset-toggle lst-overlay ${on ? 'is-on' : ''}"
      role="switch"
      aria-checked=${on ? 'true' : 'false'}
      title=${shown ? `${count} ship${count === 1 ? '' : 's'} in an EOS Live session now` : 'Mark the ships that have an EOS Live session now'}
      @click=${() => this.setOverlay(!on)}
    >
      <span class="dset-toggle-text"><strong>EOS Live session overlay</strong></span>
      ${shown ? html`<b class="lst-overlay-n" aria-label="${count} in session">${count}</b>` : nothing}
      <span class="sd-switch" aria-hidden="true"></span>
    </button>`;
  }

  private renderSessionPop(id: string): TemplateResult | typeof nothing {
    const s = this.sessions?.sessions[id];
    if (!this.overlay || !s) return nothing;
    const started = new Date(s.leg_start);
    const ago = (d: Date) => formatAge(Math.max(0, Math.round((Date.now() - d.getTime()) / 1000)));
    return html`<div class="lst-pop lst-pop-live" role="dialog" aria-label="${shipLabel(id)}, EOS Live session">
      <div class="lst-pop-title">
        <span class="lst-live-dot" aria-hidden="true"></span><span>${shipLabel(id)} · EOS Live session</span
        ><span class="lst-pop-id">${id}</span>
        <button type="button" class="lst-pop-close" aria-label="Close" @click=${() => (this.open = null)}>×</button>
      </div>
      <dl class="lst-pop-facts">
        <dt>Leg</dt><dd>${s.departure} → ${s.arrival}</dd>
        <dt>Status</dt><dd>${sessionStatusWord(s.status)}</dd>
        <dt>Leg start</dt><dd>${fmtUtcDatetime(started).slice(0, 16).replace('T', ' ')} UTC</dd>
        <dt>Running for</dt><dd>${ago(started)}</dd>
        <dt>Last update</dt><dd>${s.updated_at ? `${ago(new Date(s.updated_at))} ago` : '-'}</dd>
        ${s.test ? html`<dt>Kind</dt><dd>Test session</dd>` : nothing}
      </dl>
      <div class="lst-pop-foot"><span>From EOS Live, read again every minute</span></div>
    </div>`;
  }

  private renderPop(): TemplateResult | typeof nothing {
    if (!this.open || !this.data) return nothing;
    const [id, key] = this.open.split('/') as [string, string];
    if (key === SESSION) return this.renderSessionPop(id);
    const st = this.data.ships[id]?.sections[key];
    if (!st) return nothing;
    const label = STREAMS.find(([k]) => k === key)?.[1] ?? key;
    const signals = Object.entries(st.signals).sort(
      ([, a], [, b]) => RANK[b.status] - RANK[a.status] || (b.age_seconds ?? 0) - (a.age_seconds ?? 0),
    );
    const late = signals.filter(([, s]) => s.status !== 'ok');
    const onTime = signals.filter(([, s]) => s.status === 'ok');
    const row = ([name, s]: (typeof signals)[number]) =>
      html`<div>${this.mark(s.status)}<code title=${name}>${name}</code><span class="lst-w-${s.status}"
          >${s.frozen ? `frozen at ${s.frozen.value}` : this.age(s.age_seconds)}</span
        ></div>`;
    const frozen = frozenSignals(st);
    const mins = (s: number) => `${Math.round(s / 60)} min`;
    let body: TemplateResult;
    if (st.status === 'missing') {
      body = html`<p>No reading of any watched signal reached the Live API. The ship may be offline, or the collector does not have it.</p>`;
    } else if (st.heartbeat) {
      body = html`<p>Judged as a whole, by its newest reading. RPM is sent only when it changes, so one signal alone cannot make this stream late.</p>`;
    } else {
      body = html`<div>
        ${frozen.length
          ? html`<p>
              ${frozen.length === 1 ? 'One signal' : `${frozen.length} signals`} reported one value only in every
              reading of the last ${st.frozen_minutes ?? 30} min. ${frozen.length === 1 ? 'The sensor may be stuck.' : 'The sensors may be stuck.'}
            </p>`
          : nothing}
        <h5>Signals · ${signals.length} watched</h5>
        <div class="lst-pop-sigs">
          ${late.map(row)}${late.length && onTime.length ? html`<span class="lst-pop-sep"></span>` : nothing}
          ${onTime.slice(0, 6).map(row)}
          ${onTime.length > 6 ? html`<div><span></span><code>and ${onTime.length - 6} more on time</code><span></span></div>` : nothing}
        </div>
      </div>`;
    }
    return html`<div class="lst-pop" role="dialog" aria-label="${shipLabel(id)}, ${label}">
      <div class="lst-pop-title">
        ${this.mark(st.status)}<span>${shipLabel(id)} · ${label}</span><span class="lst-pop-id">${id}</span>
        <button type="button" class="lst-pop-close" aria-label="Close" @click=${() => (this.open = null)}>×</button>
      </div>
      <dl class="lst-pop-facts">
        <dt>Status</dt><dd class="lst-w-${st.status}">${WORD[st.status]}</dd>
        <dt>Newest reading</dt><dd>${st.age_seconds == null ? 'never' : `${this.age(st.age_seconds)} ago`}</dd>
        <dt>Late after</dt><dd>${mins(st.warning_seconds)}</dd>
        <dt>Critical after</dt><dd>${mins(st.critical_seconds)}</dd>
        ${st.frozen_minutes ? html`<dt>Frozen after</dt><dd>${st.frozen_minutes} min unchanged</dd>` : nothing}
      </dl>
      ${body}
      <div class="lst-pop-foot">
        <span>Escape or a click elsewhere closes</span>
        ${signals.length && hasFlag('live')
          ? html`<button type="button" class="btn btn-secondary lst-pop-go" @click=${() => this.openInExplore(id, st, key)}>
              Open in Explore ↗
            </button>`
          : nothing}
      </div>
    </div>`;
  }

  override render(): TemplateResult {
    const fresh =
      this.loading && !this.data
        ? 'Loading…'
        : this.since < 2
          ? 'Updated just now'
          : `Updated ${this.since} s ago`;
    return html`<section class="lst">
      <header class="lst-head">
        <h1>Live data status</h1>
        ${this.data ? this.renderChips() : nothing}
        <span class="lst-grow"></span>
        ${this.renderOverlaySwitch()}
        <input
          class="lst-search"
          type="search"
          placeholder="Find a ship"
          aria-label="Find a ship"
          .value=${this.query}
          @input=${(e: Event) => (this.query = (e.target as HTMLInputElement).value)}
        />
        <div class="lst-fresh" title="Read again every ${REFRESH_S} s while this page is open">
          <span>${fresh}</span>
          <div class="lst-bar"><i style="width: ${Math.min(100, (this.since / REFRESH_S) * 100)}%"></i></div>
        </div>
        <button
          type="button"
          class="btn btn-secondary lst-pdf"
          title="Save the board as a PDF report, as it is now"
          ?disabled=${!this.data || this.exporting}
          @click=${() => this.exportPdf()}
        >
          ${this.exporting
            ? html`<span class="spinner spinner-sm" role="status" aria-label="Making the PDF"></span>`
            : html`<svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 2v8M4.5 6.8 8 10.3l3.5-3.5M2.5 13.5h11" />
              </svg>`}
          Export PDF
        </button>
      </header>
      ${this.error ? html`<p class="lst-error" role="alert">${this.error}</p>` : nothing}
      ${this.data?.frozen_checked === false
        ? html`<p class="lst-note">The frozen-value check did not run this time. Late and missing data still show.</p>`
        : nothing}
      ${this.overlay && this.sessions?.available === false
        ? html`<p class="lst-note">EOS Live sessions could not be read. No ship shows LIVE until they can.</p>`
        : nothing}
      ${this.data
        ? this.renderTable()
        : this.loading
          ? html`<p class="lst-wait"><span class="spinner spinner-md" role="status" aria-label="Loading"></span></p>`
          : nothing}
      ${this.renderPop()}
    </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'live-status-page': LiveStatusPage;
  }
}
